import { useCallback, useEffect, useRef, useState } from 'react';
import { userPreferencesService } from '@/services/userPreferencesService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useLoanCenterDirection', 'loan-center-redesign');

export type LoanCenterDirection = 'table' | 'pipeline' | 'hybrid';

const VALID_DIRECTIONS: ReadonlySet<LoanCenterDirection> = new Set(['table', 'pipeline', 'hybrid']);
const LEGACY_LS_KEY = 'lc_direction';

function readLegacyLocalStorage(): LoanCenterDirection | null {
  try {
    const v = localStorage.getItem(LEGACY_LS_KEY);
    if (v && VALID_DIRECTIONS.has(v as LoanCenterDirection)) {
return v as LoanCenterDirection;
}
  } catch {
    /* ignore (e.g., SSR / disabled storage) */
  }
  return null;
}

function clearLegacyLocalStorage(): void {
  try {
    localStorage.removeItem(LEGACY_LS_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Server-canonical `loanCenterDirection` preference, hydrated from
 * `userPreferencesService`. While the preference is resolving, `direction`
 * is `null` — consumers MUST render a skeleton for direction-specific content
 * and not assume a default (prevents flash-of-wrong-view).
 *
 * The initial server value hydrates the view unless the user has already made
 * a newer choice. The legacy localStorage migration only runs when the server
 * has the default AND localStorage holds a non-default value; an explicit user
 * choice retires that legacy value as well.
 */
export function useLoanCenterDirection() {
  const [direction, setDirectionState] = useState<LoanCenterDirection | null>(null);
  const hasUserChoiceRef = useRef(false);

  useEffect(() => {
    // Each effect owns its response. A shared mounted flag becomes true again
    // during StrictMode setup, which lets the retired request overwrite state.
    let cancelled = false;
    (async () => {
      try {
        const result = await userPreferencesService.getPreferences();
        if (cancelled || hasUserChoiceRef.current) {
          return;
        }

        if (!result.success) {
          log.warn('getPreferences failed; falling back to table', { error: result.error?.message });
          setDirectionState('table');
          return;
        }

        const serverValue = result.data.uiPreferences.loanCenterDirection;

        // The service always returns a value (default 'table' when no row exists),
        // so we can't tell "user never saved" from "user saved table". Treat
        // a 'table' response as potentially-default: only honor legacy localStorage
        // if it holds a NON-default direction.
        if (serverValue === 'pipeline' || serverValue === 'hybrid') {
          setDirectionState(serverValue);
          clearLegacyLocalStorage();
          return;
        }

        const legacy = readLegacyLocalStorage();
        if (legacy && legacy !== 'table') {
          setDirectionState(legacy);
          clearLegacyLocalStorage();
          userPreferencesService
            .updateUIPreferences({ loanCenterDirection: legacy })
            .catch(err => log.warn('Legacy migration write failed; local state kept', { err: String(err) }));
          return;
        }

        setDirectionState(serverValue ?? 'table');
        clearLegacyLocalStorage();
      } catch (err) {
        if (cancelled || hasUserChoiceRef.current) {
          return;
        }
        log.error('Unexpected error resolving loanCenterDirection', {}, err as Error);
        setDirectionState('table');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setDirection = useCallback((next: LoanCenterDirection) => {
    hasUserChoiceRef.current = true;
    clearLegacyLocalStorage();
    setDirectionState(next);
    userPreferencesService
      .updateUIPreferences({ loanCenterDirection: next })
      .catch(err => log.error('Failed to persist loanCenterDirection', { next }, err as Error));
  }, []);

  return {
    direction,
    setDirection,
    /** True once the server preference has resolved (direction is non-null). */
    isResolved: direction !== null
  };
}
